import type { GSplatShaderEffectOptions } from './GSplatShaderEffect'
import { GSplatShaderEffect } from './GSplatShaderEffect'
import { SHOWCASE_SHADER_COMMON_GLSL } from './showcaseShaderUtils'

export interface GSplatDissolveDriftEffectOptions extends GSplatShaderEffectOptions {
  speed?: number
  motionScale?: number
  effectScale?: number
  minPixelSize?: number
}

const DEFAULT_DISSOLVE_MIN_PIXEL_SIZE = 0.5

const dissolveDriftShaderGLSL = /* glsl */ `
uniform float uTime;
uniform float uSpeed;
uniform float uMotionScale;
uniform float uEffectScale;

${SHOWCASE_SHADER_COMMON_GLSL}

float dissolveProgress(vec3 center) {
  float seed = showcaseHash13(floor(center / uEffectScale * 5.0));
  return showcaseSaturate(fract(uTime * uSpeed * 0.12 + seed * 0.72) * 1.18);
}

void modifySplatCenter(inout vec3 center) {
  float progress = dissolveProgress(center);
  vec3 seed = vec3(
    showcaseHash12(center.xy),
    showcaseHash12(center.yz),
    showcaseHash12(center.zx)
  );
  vec3 dir = normalize(vec3(seed.xy - 0.5, abs(seed.z - 0.15) + 0.2));

  center += dir * progress * progress * 1.15 * uMotionScale * uEffectScale;
}

void modifySplatCovariance(
  vec3 originalCenter,
  vec3 modifiedCenter,
  inout vec3 covA,
  inout vec3 covB
) {
  float progress = dissolveProgress(originalCenter);
  gsplatApplyUniformScale(covA, covB, max(0.03, 1.0 - progress * 0.82));
}

void modifySplatColor(vec3 center, inout vec4 color) {
  float progress = dissolveProgress(center);
  vec3 ember = mix(vec3(0.16, 0.16, 0.18), vec3(0.98, 0.52, 0.16), progress);
  color.rgb = mix(color.rgb, ember, progress * 0.78);
  color.a *= 1.0 - smoothstep(0.58, 1.0, progress);
}
`

const dissolveDriftShaderWGSL = /* wgsl */ `
fn showcaseSaturate(value: f32) -> f32 {
  return clamp(value, 0.0, 1.0);
}

fn showcaseHash13(value: vec3f) -> f32 {
  return fract(sin(dot(value, vec3f(127.1, 311.7, 74.7))) * 43758.5453123);
}

fn showcaseHash12(value: vec2f) -> f32 {
  return fract(sin(dot(value, vec2f(127.1, 311.7))) * 43758.5453123);
}

fn dissolveProgress(center: vec3f) -> f32 {
  let seed = showcaseHash13(floor(center / uniform.uEffectScale * 5.0));
  return showcaseSaturate(
    fract(uniform.uTime * uniform.uSpeed * 0.12 + seed * 0.72) * 1.18
  );
}

fn modifySplatCenter(center: ptr<function, vec3f>) {
  let progress = dissolveProgress(*center);
  let seed = vec3f(
    showcaseHash12((*center).xy),
    showcaseHash12((*center).yz),
    showcaseHash12((*center).zx)
  );
  let direction = normalize(vec3f(seed.xy - vec2f(0.5), abs(seed.z - 0.15) + 0.2));
  *center = (*center) +
    direction * progress * progress * 1.15 * uniform.uMotionScale * uniform.uEffectScale;
}

fn modifySplatRotationScale(
  originalCenter: vec3f,
  modifiedCenter: vec3f,
  rotation: ptr<function, vec4f>,
  scale: ptr<function, vec3f>
) {
  let progress = dissolveProgress(originalCenter);
  *scale = (*scale) * max(0.03, 1.0 - progress * 0.82);
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
  let progress = dissolveProgress(center);
  let ember = mix(vec3f(0.16, 0.16, 0.18), vec3f(0.98, 0.52, 0.16), progress);
  (*color) = vec4f(
    mix((*color).rgb, ember, progress * 0.78),
    (*color).a * (1.0 - smoothstep(0.58, 1.0, progress))
  );
}
`

export class GSplatDissolveDriftEffect extends GSplatShaderEffect {
  private readonly _speed: number
  private readonly _motionScale: number
  private readonly _effectScale: number
  private readonly _minPixelSize: number

  constructor(options: GSplatDissolveDriftEffectOptions = {}) {
    super({
      ...options,
      id: options.id ?? 'dissolve-drift',
    })

    this._speed = options.speed ?? 1
    this._motionScale = options.motionScale ?? 1
    this._effectScale = Math.max(0.001, options.effectScale ?? 1)
    this._minPixelSize = options.minPixelSize ?? DEFAULT_DISSOLVE_MIN_PIXEL_SIZE
    this.uniforms.minPixelSize = { value: this._minPixelSize }
  }

  override getShaderGLSL(): string {
    return dissolveDriftShaderGLSL
  }

  override getShaderWGSL(): string {
    return dissolveDriftShaderWGSL
  }

  protected override updateEffect(effectTime: number): void {
    this.setUniform('uTime', effectTime)
    this.setUniform('uSpeed', this._speed)
    this.setUniform('uMotionScale', this._motionScale)
    this.setUniform('uEffectScale', this._effectScale)
    this.setUniform('minPixelSize', this._minPixelSize)
  }
}
